<template>
  <section
    class="section-group"
    :class="{ 'is-collapsed': collapsed }"
    :data-group-key="groupKey"
  >
    <header
      class="section-group-header"
      role="button"
      tabindex="0"
      :aria-expanded="String(!collapsed)"
      @click="onHeaderClick"
      @keydown.enter.prevent="onHeaderClick"
      @keydown.space.prevent="onHeaderClick"
    >
      <span class="caret" aria-hidden="true">{{ collapsed ? '▸' : '▾' }}</span>
      <h3 class="group-title">{{ title }}</h3>
      <span class="group-count">({{ count }})</span>

      <div class="header-spacer"></div>

      <select
        v-if="!collapsed && isMobile"
        class="mobile-rows-select"
        :value="mobileRowsValue"
        @click.stop
        @change="onMobileRowsChange"
        aria-label="Visible rows"
      >
        <option value="5">Show 5</option>
        <option value="10">Show 10</option>
        <option value="20">Show 20</option>
        <option value="all">Show all</option>
      </select>

      <button
        v-if="!collapsed && showReset"
        type="button"
        class="reset-btn"
        title="Reset height"
        aria-label="Reset section height"
        @click.stop="onReset"
      >↺</button>
    </header>

    <div
      v-if="!collapsed"
      ref="body"
      class="section-group-body"
      :style="{ height: effectiveHeight != null ? effectiveHeight + 'px' : 'auto' }"
    >
      <slot></slot>
    </div>

    <div
      v-if="!collapsed && !isMobile"
      class="drag-handle"
      :class="{ 'is-dragging': isDragging, 'is-at-max': atMax }"
      role="separator"
      :aria-orientation="'horizontal'"
      :aria-label="`Resize ${title} section`"
      @mousedown.prevent="onDragStart"
      @touchstart.prevent="onDragStart"
    >
      <span class="drag-handle-line" aria-hidden="true"></span>
    </div>
  </section>
</template>

<script>
// SectionGroup
//
// Collapsible, drag-to-resize wrapper around a chunk of <table> content. The
// parent supplies a full <table> (thead + tbody) via the default slot. This
// component owns:
//   - the section header strip (collapse toggle + count + reset)
//   - the scrollable body (explicit pixel height, sticky thead inside)
//   - the drag handle (6 px visual, 12 px hit) on desktop
//   - a "Show N rows" dropdown fallback on mobile
//
// Height + collapse state are persisted to localStorage via the shared
// questionBankSections store. Writes are debounced inside the store.
//
// Default height = min(naturalContentHeight, height-of-first-10-rows). Both
// are measured from the DOM after mount and re-measured on row count changes
// via a ResizeObserver. Maximum height (drag cap) is the smaller of the
// natural content height and viewport - 200 px (reserves room for the page
// chrome above; tuned during integration).

import {
  sectionUiState,
  getSectionHeight,
  setSectionHeight,
  isSectionCollapsed,
  setSectionCollapsed,
  resetSectionHeight,
} from './_data/questionBankSections.js';

const DEFAULT_ROW_COUNT = 10;
const VIEWPORT_RESERVE_PX = 200;
const MOBILE_BREAKPOINT_PX = 640;

export default {
  name: 'SectionGroup',
  props: {
    groupKey: {
      type: [String, Number],
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    count: {
      type: Number,
      default: 0,
    },
    // When true, collapse state is component-local and NOT persisted to
    // localStorage. Each mount of this component resets to `initialCollapsed`.
    // The drag-height still uses the shared store — only the collapse
    // toggle is ephemeral. Use case: the AddCardModal picker, where each
    // open should start with sections collapsed regardless of how the
    // user organised the standalone bank page.
    ephemeralCollapse: {
      type: Boolean,
      default: false,
    },
    // Initial collapse state when `ephemeralCollapse` is true. Ignored
    // otherwise (the store + first-visit logic owns the default).
    initialCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Measured from the DOM after mount; updated when row count changes.
      naturalHeight: null,     // px — height of the full table content
      tenRowsHeight: null,     // px — height up to the bottom of the 10th row
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
      isMobile: typeof window !== 'undefined'
        ? window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches
        : false,
      // Drag state. localHeight is the live preview height during a drag;
      // committed to the store on drag end.
      isDragging: false,
      dragStartY: 0,
      dragStartHeight: 0,
      localHeight: null,
      // Component-local collapse, used only when `ephemeralCollapse` is
      // true. Otherwise the `collapsed` computed reads from the store.
      localCollapsed: this.ephemeralCollapse ? this.initialCollapsed : false,
      // Cleanup handles.
      _resizeObserver: null,
      _mediaQueryList: null,
      _onWindowResize: null,
      _onMediaChange: null,
      _onDragMove: null,
      _onDragEnd: null,
    };
  },
  computed: {
    storedHeight() {
      // Pull through the reactive store so this computed re-runs when the
      // user resets or another tab modifies the value.
      return getSectionHeight(this.groupKey);
    },
    collapsed() {
      if (this.ephemeralCollapse) return this.localCollapsed;
      // Same channel: reactive on the store.
      // Reading sectionUiState directly here would also work, but the
      // helper keeps the lookup centralized.
      void sectionUiState.collapsed[String(this.groupKey)];
      return isSectionCollapsed(this.groupKey);
    },
    defaultHeight() {
      // Defaults need both measurements before they mean anything.
      if (this.naturalHeight == null || this.tenRowsHeight == null) return null;
      return Math.min(this.naturalHeight, this.tenRowsHeight);
    },
    maxHeight() {
      if (this.naturalHeight == null) return null;
      return Math.min(this.naturalHeight, this.viewportHeight - VIEWPORT_RESERVE_PX);
    },
    effectiveHeight() {
      // Drag-in-progress wins over stored value wins over default.
      if (this.localHeight != null) return this.localHeight;
      if (this.storedHeight != null && this.maxHeight != null) {
        return Math.min(this.storedHeight, this.maxHeight);
      }
      return this.defaultHeight;
    },
    showReset() {
      return this.storedHeight != null;
    },
    atMax() {
      if (this.effectiveHeight == null || this.maxHeight == null) return false;
      return this.effectiveHeight >= this.maxHeight - 1;
    },
    mobileRowsValue() {
      // "all" if the current effective height >= natural; otherwise the
      // closest preset to what's stored.
      if (this.effectiveHeight == null || this.naturalHeight == null) return '10';
      if (this.effectiveHeight >= this.naturalHeight - 1) return 'all';
      // Don't try to guess between 5/10/20 — just show the closest.
      // (The dropdown is informational + selectable; the precise pixel
      // value still lives in the store.)
      const target = this.effectiveHeight;
      const candidates = [5, 10, 20];
      let best = '10';
      let bestDiff = Infinity;
      for (const c of candidates) {
        const px = this.heightForRowCount(c);
        if (px == null) continue;
        const diff = Math.abs(px - target);
        if (diff < bestDiff) { bestDiff = diff; best = String(c); }
      }
      return best;
    },
  },
  watch: {
    count() {
      // Row count changed (filters, archive, etc.). Re-measure on the next
      // tick so the slot has finished re-rendering.
      this.$nextTick(() => this.measure());
    },
    collapsed(now) {
      // When the user re-expands, the body wasn't in the DOM while
      // collapsed so measurements are stale. Re-measure once it's back.
      if (!now) this.$nextTick(() => this.measure());
    },
  },
  mounted() {
    this.$nextTick(() => this.measure());

    // Window resize — recompute the viewport cap (the drag max).
    this._onWindowResize = () => {
      this.viewportHeight = window.innerHeight;
    };
    window.addEventListener('resize', this._onWindowResize);

    // Media query — switch between drag handle and mobile dropdown.
    this._mediaQueryList = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT_PX}px)`,
    );
    this._onMediaChange = (e) => { this.isMobile = e.matches; };
    // Older Safari only supports addListener/removeListener.
    if (this._mediaQueryList.addEventListener) {
      this._mediaQueryList.addEventListener('change', this._onMediaChange);
    } else {
      this._mediaQueryList.addListener(this._onMediaChange);
    }

    // ResizeObserver — re-measure when the slot's content actually changes
    // size (e.g. a long title wraps differently after a language switch).
    if (this.$refs.body && typeof ResizeObserver === 'function') {
      this._resizeObserver = new ResizeObserver(() => {
        // Skip measurement during a drag — the body is being held at a
        // fixed height we set, so its scrollHeight is what we want, but
        // measure() would also recompute tenRowsHeight which is fine.
        // Still, debounce a touch by going through nextTick.
        if (this.isDragging) return;
        this.$nextTick(() => this.measureNaturalOnly());
      });
      // Observe the inner table so we catch row content changes; the body
      // itself is fixed-height when not collapsed and would never resize.
      const inner = this.$refs.body.firstElementChild;
      if (inner) this._resizeObserver.observe(inner);
    }
  },
  beforeUnmount() {
    if (this._onWindowResize) {
      window.removeEventListener('resize', this._onWindowResize);
    }
    if (this._mediaQueryList) {
      if (this._mediaQueryList.removeEventListener) {
        this._mediaQueryList.removeEventListener('change', this._onMediaChange);
      } else {
        this._mediaQueryList.removeListener(this._onMediaChange);
      }
    }
    if (this._resizeObserver) this._resizeObserver.disconnect();
    this.cleanupDragListeners();
  },
  methods: {
    onHeaderClick() {
      if (this.ephemeralCollapse) {
        this.localCollapsed = !this.localCollapsed;
      } else {
        setSectionCollapsed(this.groupKey, !this.collapsed);
      }
    },

    onReset() {
      resetSectionHeight(this.groupKey);
    },

    /**
     * Re-measure both naturalHeight and tenRowsHeight from the DOM.
     * Safe to call repeatedly; uses getBoundingClientRect for accuracy
     * across variable-height rows (titles wrap, badges add lines, etc.).
     */
    measure() {
      const body = this.$refs.body;
      if (!body) return;
      const inner = body.firstElementChild;
      if (!inner) return;

      // Natural height = the full content height with no scroll. scrollHeight
      // gives that even when the parent is clipping with overflow:auto.
      this.naturalHeight = inner.scrollHeight;

      // 10-rows height: top of body to bottom of 10th row (or last row if
      // fewer than 10). Walk the tbody, since the slot is a <table>.
      const tbody = inner.querySelector('tbody');
      const thead = inner.querySelector('thead');
      const rows = tbody ? tbody.querySelectorAll('tr') : [];
      if (!rows.length) {
        // No rows yet — fall back to natural so we don't show 0-height.
        this.tenRowsHeight = this.naturalHeight;
        return;
      }
      const bodyRect = body.getBoundingClientRect();
      const targetIdx = Math.min(DEFAULT_ROW_COUNT - 1, rows.length - 1);
      const targetRow = rows[targetIdx];
      const targetRect = targetRow.getBoundingClientRect();
      const theadH = thead ? thead.getBoundingClientRect().height : 0;
      // bottom-of-target relative to body top, plus a 1 px buffer to make
      // sure we don't trigger an unnecessary scrollbar.
      this.tenRowsHeight = Math.ceil(
        (targetRect.bottom - bodyRect.top) + 1,
      );
      // Floor of "header + 1 row" so the minimum never collapses below
      // something usable. (Used by the drag clamp.)
      void theadH;
    },

    /**
     * Lightweight re-measure for when only the content resized (e.g.
     * locale switch made a title wrap). Skips tenRowsHeight since the
     * row count hasn't changed; only the natural total may have.
     */
    measureNaturalOnly() {
      const body = this.$refs.body;
      if (!body) return;
      const inner = body.firstElementChild;
      if (!inner) return;
      this.naturalHeight = inner.scrollHeight;
    },

    heightForRowCount(n) {
      // Pixel height for the first N rows + the column header. Used by
      // the mobile "Show N rows" dropdown.
      const body = this.$refs.body;
      if (!body) return null;
      const inner = body.firstElementChild;
      if (!inner) return null;
      const tbody = inner.querySelector('tbody');
      const rows = tbody ? tbody.querySelectorAll('tr') : [];
      if (!rows.length) return null;
      const bodyRect = body.getBoundingClientRect();
      const targetIdx = Math.min(n - 1, rows.length - 1);
      const targetRow = rows[targetIdx];
      const targetRect = targetRow.getBoundingClientRect();
      return Math.ceil((targetRect.bottom - bodyRect.top) + 1);
    },

    onMobileRowsChange(e) {
      const v = e.target.value;
      if (v === 'all') {
        if (this.naturalHeight != null) {
          setSectionHeight(this.groupKey, this.naturalHeight);
        }
        return;
      }
      const n = parseInt(v, 10);
      const px = this.heightForRowCount(n);
      if (px != null) setSectionHeight(this.groupKey, px);
    },

    /* ────── Drag handlers ────── */

    onDragStart(e) {
      if (this.effectiveHeight == null) return;
      this.isDragging = true;
      this.dragStartY = this._pointerY(e);
      this.dragStartHeight = this.effectiveHeight;
      this.localHeight = this.effectiveHeight;

      this._onDragMove = (ev) => this.onDragMove(ev);
      this._onDragEnd = (ev) => this.onDragEnd(ev);
      window.addEventListener('mousemove', this._onDragMove);
      window.addEventListener('mouseup', this._onDragEnd);
      window.addEventListener('touchmove', this._onDragMove, { passive: false });
      window.addEventListener('touchend', this._onDragEnd);
      // Lock selection during drag — otherwise the text under the cursor
      // gets highlighted as the user drags downward.
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';
    },

    onDragMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const y = this._pointerY(e);
      const delta = y - this.dragStartY;
      const proposed = this.dragStartHeight + delta;
      const min = this.minHeight();
      const max = this.maxHeight != null ? this.maxHeight : proposed;
      this.localHeight = Math.max(min, Math.min(max, proposed));
    },

    onDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      const finalHeight = this.localHeight;
      this.localHeight = null;
      this.cleanupDragListeners();
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (finalHeight != null) {
        setSectionHeight(this.groupKey, Math.round(finalHeight));
      }
    },

    cleanupDragListeners() {
      if (this._onDragMove) {
        window.removeEventListener('mousemove', this._onDragMove);
        window.removeEventListener('touchmove', this._onDragMove);
        this._onDragMove = null;
      }
      if (this._onDragEnd) {
        window.removeEventListener('mouseup', this._onDragEnd);
        window.removeEventListener('touchend', this._onDragEnd);
        this._onDragEnd = null;
      }
    },

    _pointerY(e) {
      if (e.touches && e.touches.length) return e.touches[0].clientY;
      return e.clientY;
    },

    minHeight() {
      // One row + header chrome. We approximate as 80 px; the spec says
      // "1 row plus the headers" and exact px depends on row content.
      // A floor of 80 keeps the drag from collapsing to a hairline.
      return 80;
    },
  },
};
</script>

<style scoped>
.section-group {
  --color-bg-card: #ffffff;
  --color-bg-soft: #fafaf7;
  --color-bg-muted: #f5f4ef;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  margin-bottom: 1rem;
  overflow: hidden;
  font-family: var(--font-sans);
}

.section-group.is-collapsed {
  /* Drop the bottom drag-handle area entirely so the box ends right
     under the header. */
  padding-bottom: 0;
}

/* ───────── Header ───────── */
.section-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--color-bg-soft);
  border-bottom: 0.5px solid var(--color-border);
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.section-group.is-collapsed .section-group-header {
  border-bottom-color: transparent;
}

.section-group-header:hover {
  background: var(--color-bg-muted);
}

.section-group-header:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: -2px;
}

.caret {
  display: inline-block;
  width: 14px;
  font-size: 11px;
  color: var(--color-text-secondary);
  text-align: center;
  line-height: 1;
}

.group-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.group-count {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-tertiary);
}

.header-spacer {
  flex: 1;
}

.reset-btn {
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: 4px;
  padding: 2px 7px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: var(--color-text-tertiary);
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.reset-btn:hover {
  background: var(--color-bg-card);
  color: var(--color-accent);
  border-color: var(--color-border);
}

.mobile-rows-select {
  font-family: inherit;
  font-size: 12px;
  padding: 4px 6px;
  border: 0.5px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  cursor: pointer;
}

/* ───────── Body ───────── */
.section-group-body {
  overflow-y: auto;
  overflow-x: auto;
  /* The slot's <table> uses position:sticky on thead; this element is
     the scroll context that sticky needs. */
  background: var(--color-bg-card);
}

/* Mac-style thin scrollbar — only when supported. Falls back to native
   on Windows/Linux. */
.section-group-body::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.section-group-body::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 5px;
  border: 2px solid var(--color-bg-card);
}

.section-group-body::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.section-group-body::-webkit-scrollbar-track {
  background: transparent;
}

/* ───────── Drag handle ───────── */
.drag-handle {
  position: relative;
  height: 12px;       /* hit zone */
  cursor: row-resize;
  user-select: none;
  background: transparent;
  transition: background 0.15s;
}

.drag-handle-line {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 2px;
  border-radius: 2px;
  background: transparent;
  transition: background 0.15s;
}

.drag-handle:hover .drag-handle-line,
.drag-handle.is-dragging .drag-handle-line {
  background: var(--color-border-strong);
}

.drag-handle:hover {
  background: var(--color-bg-soft);
}

.drag-handle.is-at-max {
  cursor: default;
}

.drag-handle.is-at-max .drag-handle-line {
  background: transparent;
}

@media (max-width: 640px) {
  .drag-handle { display: none; }
}
</style>
